import { DbRef, SupplyType } from '@caresend/types';
import { nullishFilter, objectFilter, deduplicateArray, getPostalCodeByLocation } from '@caresend/utils';
import update from 'immutability-helper';
import { FeatureFlagSetting } from '../../../components/specific/dev/AdvancedSettings/subViews/FeatureFlags/model.js';
import { firebaseBind } from '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastErrorAndReport } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import { getStore } from '../../helpers/initStore.js';
import { initExtendedModule } from '../helpers.js';
import { applyFeatureFlagOverrides } from './helpers.js';

/* eslint-disable max-classes-per-file */
const variablesState = {
    dbFeatureFlags: {},
    featureFlagOverrides: {},
    featureFlags: {},
    variables: undefined,
};
const variablesMutations = {
    'variables/SET_FEATURE_FLAG_OVERRIDE': (state, { flag, setting }) => {
        const updateSpec = setting === FeatureFlagSetting.NONE
            ? { $unset: [flag] }
            : { [flag]: { $set: setting === FeatureFlagSetting.TRUE } };
        state.featureFlagOverrides = update(state.featureFlagOverrides, updateSpec);
        const featureFlags = applyFeatureFlagOverrides(state.dbFeatureFlags, state.featureFlagOverrides);
        state.featureFlags = featureFlags;
    },
    'variables/SET_VARIABLES': (state, variables) => {
        const variablesWithoutFeatureFlags = update(variables, {
            $unset: ['featureFlags'],
        });
        state.dbFeatureFlags = variables.featureFlags;
        const featureFlags = applyFeatureFlagOverrides(state.dbFeatureFlags, state.featureFlagOverrides);
        state.featureFlags = featureFlags;
        state.variables = variablesWithoutFeatureFlags;
    },
};
const variablesActions = {
    'variables/bindVariables': async ({ commit }) => {
        try {
            await firebaseBind(DbRef.VARIABLES, (variables) => {
                commit('variables/SET_VARIABLES', variables);
            });
        }
        catch (error) {
            toastErrorAndReport(error);
        }
    },
};
const variablesGetters = {
    'variables/getCityRecruitmentFilters': (state) => { var _a; return (_a = state.variables) === null || _a === void 0 ? void 0 : _a.recruitmentFilters.cities; },
    'variables/getEndTimeInstructions': (state) => {
        var _a, _b, _c, _d, _e;
        if (!((_a = state.variables) === null || _a === void 0 ? void 0 : _a.timeInstructions))
            return 0;
        return (_e = (_d = (_b = state.variables) === null || _b === void 0 ? void 0 : _b.timeInstructions[((_c = state.variables) === null || _c === void 0 ? void 0 : _c.timeInstructions.length) - 1]) === null || _d === void 0 ? void 0 : _d.endTime) !== null && _e !== void 0 ? _e : 0;
    },
    'variables/getFeatureFlagByName': (state) => (flagName) => { var _a; return (_a = state.featureFlags[flagName]) !== null && _a !== void 0 ? _a : false; },
    /** Defaults to 1400 (2:00 PM) if not found */
    'variables/getFridayBrickAndMortarCutoffTime': (state) => { var _a, _b; return (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.fridayBrickAndMortarCutoffTime) !== null && _b !== void 0 ? _b : 1400; },
    /** Defaults to 700 (7:00 AM) and 1500 (3:00 PM) if not found */
    'variables/getFacilityTimesFridayTimeSlotRestrictions': (state) => { var _a, _b; return (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.facilityTimesFridayTimeSlotRestrictions) !== null && _b !== void 0 ? _b : { start: 700, end: 1500 }; },
    'variables/getLabDrawTask': (state) => variablesGetters['variables/getTaskByName'](state)('venipuncture'),
    'variables/getUrineCollectionTask': (state) => variablesGetters['variables/getTaskByName'](state)('Urine collection'),
    'variables/getMedicalTitlesByRole': (state) => (role) => {
        var _a, _b;
        return Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.medicalTitles) !== null && _b !== void 0 ? _b : {})
            .filter((title) => title.roles[role]);
    },
    'variables/getBasePayForMedicalTitle': (state) => (title) => { var _a, _b, _c; return ((_c = (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.medicalTitles[title.toLocaleLowerCase()]) === null || _b === void 0 ? void 0 : _b.basePay) !== null && _c !== void 0 ? _c : 0) / 100; },
    'variables/getMinimumTitleByTaskTypeID': (state) => (taskTypeID, short) => {
        const taskType = variablesGetters['variables/getTaskTypeByID'](state)(taskTypeID);
        if (!taskType)
            return;
        const titleRanking = ['rn', 'lvn', 'phlebotomist'];
        const taskTypeTitles = Object.values(taskType.titles);
        const minimumTitleIndex = Math.max(...taskTypeTitles.map((title) => titleRanking.indexOf(title)));
        let title;
        if (minimumTitleIndex === -1) {
            [title] = taskType.titles;
        }
        else {
            title = titleRanking[minimumTitleIndex];
        }
        if (short && title === 'phlebotomist') {
            title = title.substring(0, 2);
        }
        return title;
    },
    'variables/getNLCStates': (state) => {
        var _a;
        if (!state.variables)
            return {};
        return (_a = state.variables.NLCStates) !== null && _a !== void 0 ? _a : {};
    },
    'variables/getProductBySlugName': (state) => (slugName) => {
        var _a, _b, _c;
        if (!slugName)
            return null;
        return (_c = Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.products) !== null && _b !== void 0 ? _b : {})
            .find((product) => { var _a; return ((_a = product.info) === null || _a === void 0 ? void 0 : _a.slugName) === slugName; })) !== null && _c !== void 0 ? _c : null;
    },
    'variables/getProductByID': (state) => (id) => {
        var _a, _b;
        if (!id)
            return null;
        return (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.products[id]) !== null && _b !== void 0 ? _b : null;
    },
    'variables/getProductInfo': (state) => (task, productIDSpecified) => {
        var _a, _b;
        if (!task)
            return null;
        if (productIDSpecified) {
            return (_b = (_a = variablesGetters['variables/getProductByID'](state)(productIDSpecified)) === null || _a === void 0 ? void 0 : _a.info) !== null && _b !== void 0 ? _b : null;
        }
        let productInfo = task.info;
        const products = variablesGetters['variables/getProductsByTaskID'](state)(task.id);
        Object.values(products !== null && products !== void 0 ? products : {}).forEach((productID) => {
            var _a;
            const product = variablesGetters['variables/getProductByID'](state)(productID);
            if ((_a = product === null || product === void 0 ? void 0 : product.info) === null || _a === void 0 ? void 0 : _a.slugName) {
                productInfo = product === null || product === void 0 ? void 0 : product.info;
            }
        });
        return productInfo;
    },
    'variables/getProductNamesByIDs': (state) => (productIDs) => {
        const productNames = productIDs.reduce((names, productID, index) => {
            var _a, _b, _c;
            const name = (_c = (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.products[productID]) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : 'Unknown product';
            const separator = index !== productIDs.length - 1 ? ', ' : '';
            return names + name + separator;
        }, '');
        return productNames;
    },
    'variables/getProductsByIDs': (state) => (ids) => ids
        .map((id) => { var _a; return (_a = state.variables) === null || _a === void 0 ? void 0 : _a.products[id]; })
        .filter(nullishFilter)
        // Case insensitive sort
        .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })),
    'variables/getProductsByTaskID': (state) => (id) => {
        var _a, _b;
        if (!id)
            return [];
        return Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.products) !== null && _b !== void 0 ? _b : {})
            .filter((product) => product.taskID === id).map((product) => product.id);
    },
    'variables/getProductsByTaskName': (state) => (name) => {
        var _a, _b, _c;
        const task = variablesGetters['variables/getTaskByName'](state)(name);
        if (!task)
            return [];
        return (_c = Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.products) !== null && _b !== void 0 ? _b : {})
            .filter((product) => product.taskID === task.id)) !== null && _c !== void 0 ? _c : [];
    },
    'variables/getServiceRegionByID': (state) => (id) => {
        var _a, _b;
        if (!id)
            return null;
        return (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.serviceRegions[id]) !== null && _b !== void 0 ? _b : null;
    },
    'variables/getServiceRegionGroupByID': (state) => (id) => {
        var _a, _b;
        if (!id)
            return null;
        return (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.serviceRegionGroups[id]) !== null && _b !== void 0 ? _b : null;
    },
    'variables/getServiceRegionGroupByServiceRegionID': (state) => (id) => {
        var _a, _b, _c;
        if (!id)
            return null;
        const serviceRegionGroups = Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.serviceRegionGroups) !== null && _b !== void 0 ? _b : {})
            .filter((group) => Object.keys(group.serviceRegions).includes(id));
        return (_c = serviceRegionGroups[0]) !== null && _c !== void 0 ? _c : null;
    },
    'variables/getServiceRegionsByServiceRegionGroupID': (state) => (id) => {
        var _a, _b, _c;
        const serviceRegionGroup = (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.serviceRegionGroups[id]) !== null && _b !== void 0 ? _b : null;
        if (!serviceRegionGroup)
            return undefined;
        return Object.keys((_c = serviceRegionGroup.serviceRegions) !== null && _c !== void 0 ? _c : {})
            .map((regionID) => { var _a; return (_a = state.variables) === null || _a === void 0 ? void 0 : _a.serviceRegions[regionID]; })
            .filter(nullishFilter);
    },
    'variables/getServiceRegionGroupsByStateCode': (state) => (stateCode) => {
        var _a, _b;
        return Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.serviceRegionGroups) !== null && _b !== void 0 ? _b : {})
            .filter((serviceRegionGroup) => serviceRegionGroup.stateCode === stateCode);
    },
    'variables/getServiceRegionGroupTimeZone': (state) => (serviceRegionGroupID) => { var _a, _b, _c; return (_c = (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.serviceRegionGroups[serviceRegionGroupID]) === null || _b === void 0 ? void 0 : _b.timeZone) !== null && _c !== void 0 ? _c : ''; },
    'variables/getStartTimeInstructions': (state) => {
        var _a, _b, _c, _d;
        if (!((_a = state.variables) === null || _a === void 0 ? void 0 : _a.timeInstructions))
            return 0;
        return (_d = (_c = (_b = state.variables) === null || _b === void 0 ? void 0 : _b.timeInstructions[0]) === null || _c === void 0 ? void 0 : _c.startTime) !== null && _d !== void 0 ? _d : 0;
    },
    'variables/getSupplyByID': (state) => (id) => { var _a, _b; return (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.supplies[id]) !== null && _b !== void 0 ? _b : null; },
    'variables/getSupplyByName': (state) => (name) => {
        var _a, _b, _c;
        return (_c = Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.supplies) !== null && _b !== void 0 ? _b : {})
            .find((supply) => supply.name === name)) !== null && _c !== void 0 ? _c : null;
    },
    'variables/getShiftCheckInThreshold': (state) => { var _a, _b; return (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.shiftCheckInThreshold) !== null && _b !== void 0 ? _b : 0; },
    'variables/getShipmentBoxSupplies': (state) => {
        var _a, _b;
        return objectFilter((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.supplies) !== null && _b !== void 0 ? _b : {}, (supply) => supply.type === SupplyType.SHIPMENT_BOX);
    },
    'variables/getTaskByID': (state) => (id) => { var _a, _b; return id ? (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.tasks[id]) !== null && _b !== void 0 ? _b : null : null; },
    'variables/getTaskByName': (state) => (name) => {
        var _a, _b, _c;
        return (_c = Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.tasks) !== null && _b !== void 0 ? _b : {})
            .find((task) => task.info.name === name)) !== null && _c !== void 0 ? _c : null;
    },
    /**
     * Get a task by its product's slugname. If no product is found, fallback
     * to looking for a task with the given slugName instead.
     */
    'variables/getTaskByProductSlugName': (state) => (productName) => {
        const product = variablesGetters['variables/getProductBySlugName'](state)(productName);
        if (product)
            return variablesGetters['variables/getTaskByID'](state)(product.taskID);
        return variablesGetters['variables/getTaskBySlugName'](state)(productName);
    },
    'variables/getTaskBySlugName': (state) => (slugName) => {
        var _a, _b, _c;
        if (!slugName)
            return null;
        return (_c = Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.tasks) !== null && _b !== void 0 ? _b : {})
            .find((task) => { var _a; return ((_a = task.info) === null || _a === void 0 ? void 0 : _a.slugName) === slugName; })) !== null && _c !== void 0 ? _c : null;
    },
    'variables/getTasksByTaskTypeID': (state) => (taskTypeID) => {
        var _a, _b;
        return Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.tasks) !== null && _b !== void 0 ? _b : {}).filter((task) => task.taskTypeID === taskTypeID);
    },
    'variables/getTaskTypeByID': (state) => (id) => { var _a, _b; return (_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.taskTypes[id]) !== null && _b !== void 0 ? _b : null; },
    'variables/getTaskTypeByName': (state) => (name) => {
        var _a, _b, _c;
        if (!name)
            return null;
        return (_c = Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.taskTypes) !== null && _b !== void 0 ? _b : {})
            .find((taskType) => taskType.info.name === name)) !== null && _c !== void 0 ? _c : null;
    },
    'variables/getTaskTypesByTitle': (state) => (title) => {
        var _a, _b;
        return Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.taskTypes) !== null && _b !== void 0 ? _b : {})
            .filter((taskType) => taskType.titles.includes(title));
    },
    'variables/getUserAllowedTaskTypes': (state, _getters, rootState) => {
        var _a, _b, _c, _d;
        const userTitle = (_b = (_a = rootState.auth.user) === null || _a === void 0 ? void 0 : _a.professionalInfo) === null || _b === void 0 ? void 0 : _b.title;
        if (!userTitle)
            return [];
        const proTasks = Object.values((_d = (_c = state.variables) === null || _c === void 0 ? void 0 : _c.proTasksList) !== null && _d !== void 0 ? _d : {})
            .map((taskID) => { var _a; return (_a = variablesGetters['variables/getTaskByID'](state)(taskID)) === null || _a === void 0 ? void 0 : _a.taskTypeID; });
        const taskTypes = deduplicateArray(proTasks)
            .filter(nullishFilter)
            .map((taskTypeID) => variablesGetters['variables/getTaskTypeByID'](state)(taskTypeID))
            .filter((taskType) => taskType === null || taskType === void 0 ? void 0 : taskType.titles.includes(userTitle))
            .filter(nullishFilter);
        return taskTypes;
    },
    'variables/getServiceRegionGroupByLocation': (state) => (location) => {
        var _a, _b, _c;
        if (!location)
            return null;
        const postalCode = getPostalCodeByLocation(location);
        if (!postalCode)
            return null;
        const serviceRegionsOnLocation = Object.values((_b = (_a = state.variables) === null || _a === void 0 ? void 0 : _a.serviceRegions) !== null && _b !== void 0 ? _b : {}).filter((region) => { var _a; return Object.keys((_a = region.postalCodes) !== null && _a !== void 0 ? _a : {}).includes(postalCode); });
        if (!serviceRegionsOnLocation)
            return null;
        const region = serviceRegionsOnLocation[0];
        if (!region)
            return null;
        const group = (_c = state.variables) === null || _c === void 0 ? void 0 : _c.serviceRegionGroups[region.serviceRegionGroupID];
        if (!group)
            return null;
        return group;
    },
    // Check an arbitrary variable that we expect to exist to ensure variables
    // have loaded.
    'variables/variablesLoaded': (state) => { var _a; return ((_a = state.variables) === null || _a === void 0 ? void 0 : _a.timeInstructions.length) !== 0; },
};
const variablesModule = {
    state: variablesState,
    mutations: variablesMutations,
    actions: variablesActions,
    getters: variablesGetters,
};
const initVariablesModule = (moduleExtensions) => initExtendedModule(variablesModule, moduleExtensions);
/**
 * Returns boolean of whether a given feature flag is set to true in the
 * variables module. Respects local overrides.
 */
const flagOn = (flag) => getStore().getters['variables/getFeatureFlagByName'](flag);
/**
 * Returns boolean of whether a given feature flag is missing or set to false in
 * the variables module. Respects local overrides.
 */
const flagOff = (flag) => !flagOn(flag);

export { flagOff, flagOn, initVariablesModule };
